import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Form, Select, Button, message, Typography, Space, Input, Alert, Spin, InputNumber } from 'antd';
import { TournamentService } from '@/services/tournament.service';
import { TournamentCategory } from '@/types/api.types';
import { logger } from '@/utils/logger';
import { sortTournamentCategories } from '@/utils/tournamentCategorySort';

const { Title } = Typography;
const { Option } = Select;

// Not used - using TournamentCategory instead
// interface Category {
//     id: number;
//     name: string;
//     ageMin: number;
//     ageMax: number;
//     weightMin: number;
//     weightMax: number;
//     gender: 'MALE' | 'FEMALE';
// }

interface RegistrationFormData {
    categoryId: number;
    weight: number;
    age: number;
    club: string;
    coach: string;
}

export const TournamentRegistration: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [categories, setCategories] = useState<TournamentCategory[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const fetchCategories = async () => {
            if (!id) return;

            try {                
                // Use the TournamentService to fetch categories
                const tournamentId = parseInt(id);
                const categoriesData = await TournamentService.getTournamentCategories(tournamentId);

                if (Array.isArray(categoriesData)) {
                    setCategories(sortTournamentCategories(categoriesData));

                    if (categoriesData.length === 0) {
                        logger.warn(`No categories found for tournament ${id}`);
                    }
                } else {
                    logger.error('Invalid categories data format:', categoriesData);
                    setCategories([]);
                }
            } catch (error) {
                logger.error('Error fetching tournament categories:', error);
                // Don't show error message to user, just log it and set empty categories
                setCategories([]);
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, [id]);

    const onFinish = async (values: RegistrationFormData) => {
        if (!id) return;

        setSubmitting(true);
        try {            
            // Use the TournamentService to register
            await TournamentService.registerParticipant(parseInt(id), values);

            message.success('Регистрация успешно завершена');
            navigate(`/tournaments/${id}`);
        } catch (error) {
            logger.error('Registration error:', error);
            message.error('Ошибка при регистрации');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Card>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Spin size="large" />
                </div>
            </Card>
        );
    }

    if (categories.length === 0) {
        return (
            <Card>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Title level={2}>Регистрация на турнир</Title>
                    <Alert
                        message="Регистрация невозможна"
                        description="Для данного турнира еще не созданы категории. Пожалуйста, попробуйте позже."
                        type="warning"
                        showIcon
                    />
                    <Button onClick={() => navigate(`/tournaments/${id}`)}>
                        Вернуться к турниру
                    </Button>
                </Space>
            </Card>
        );
    }

    return (
        <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Title level={2}>Регистрация на турнир</Title>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    disabled={submitting}
                >
                    <Form.Item
                        name="categoryId"
                        label="Категория"
                        rules={[{ required: true, message: 'Выберите категорию' }]}
                    >
                        <Select
                            showSearch
                            placeholder="Выберите категорию"
                            optionFilterProp="children"
                        >
                            {categories.map(category => (
                                <Option
                                    key={category.id}
                                    value={category.id}
                                >
                                    {`${category.name} (${category.minAge}-${category.maxAge} лет, ${category.weightFrom}-${category.weightTo} кг)`}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="weight"
                        label="Вес (кг)"
                        rules={[
                            { required: true, message: 'Введите вес' },
                            { type: 'number', min: 0, message: 'Вес должен быть положительным числом' }
                        ]}
                    >
                        <InputNumber style={{ width: '100%' }} min={0} precision={1} />
                    </Form.Item>

                    <Form.Item
                        name="age"
                        label="Возраст"
                        rules={[
                            { required: true, message: 'Введите возраст' },
                            { type: 'number', min: 0, message: 'Возраст должен быть положительным числом' }
                        ]}
                    >
                        <InputNumber style={{ width: '100%' }} min={0} precision={0} />
                    </Form.Item>

                    <Form.Item
                        name="club"
                        label="Клуб"
                        rules={[{ required: true, message: 'Введите название клуба' }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="coach"
                        label="Тренер"
                        rules={[{ required: true, message: 'Введите имя тренера' }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={submitting}
                            >
                                Зарегистрироваться
                            </Button>
                            <Button
                                onClick={() => navigate(`/tournaments/${id}`)}
                                disabled={submitting}
                            >
                                Отмена
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Space>
        </Card>
    );
};
